import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { userFacingError } from '../lib/userFacingErrors';

/**
 * Background task types
 */
export const TASK_TYPES = {
  KEYFRAME_CREATE: 'keyframe_create',
  KEYFRAME_EDIT: 'keyframe_edit',
  ASSET_CREATE: 'asset_create',
  ASSET_EDIT: 'asset_edit',
  ORG_ASSET_CREATE: 'org_asset_create',
  ORG_ASSET_EDIT: 'org_asset_edit',
};

/**
 * Task status
 */
export const TASK_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

const initialState = {
  // Active background tasks: Map of taskId -> task object
  // task: { id, type, status, sceneId?, keyframeId?, assetId?, prompt, createdAt, completedAt?, result?, error? }
  tasks: {},
  
  // Completed tasks that haven't been viewed/dismissed by user
  // These appear in the "New Content" section
  newContent: [],
};

/**
 * Store for tracking background generation tasks (keyframes, assets).
 * Enables non-blocking UX where users can continue working while generations complete.
 */
const STORAGE_KEY = 'bookthing-background-tasks';

const pruneStaleTasks = (state) => {
  const now = Date.now();
  const maxAgeMs = 1000 * 60 * 60 * 24 * 7; // 7 days
  const tasks = Object.fromEntries(
    Object.entries(state.tasks || {}).filter(([, task]) => {
      if (!task?.createdAt) return true;
      return now - task.createdAt < maxAgeMs;
    })
  );
  const newContent = Array.isArray(state.newContent)
    ? state.newContent.filter((item) => {
        if (!item?.createdAt) return true;
        return now - item.createdAt < maxAgeMs;
      })
    : [];
  return { ...state, tasks, newContent };
};

export const useBackgroundTasksStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

  /**
   * Add a new background task
   */
  addTask: (task) => set((state) => ({
    tasks: {
      ...state.tasks,
      [task.id]: {
        ...task,
        status: TASK_STATUS.PENDING,
        createdAt: Date.now(),
      },
    },
  })),

  /**
   * Update an existing task
   */
  updateTask: (taskId, updates) => set((state) => {
    if (!state.tasks[taskId]) return state;
    return {
      tasks: {
        ...state.tasks,
        [taskId]: {
          ...state.tasks[taskId],
          ...updates,
        },
      },
    };
  }),

  /**
   * Mark a task as sent to background (user dismissed the view while generating)
   * This prevents auto-navigation when the task completes
   */
  markTaskAsBackground: (taskId) => set((state) => {
    if (!state.tasks[taskId]) return state;
    return {
      tasks: {
        ...state.tasks,
        [taskId]: {
          ...state.tasks[taskId],
          sentToBackground: true,
        },
      },
    };
  }),

  /**
   * Check if a task was sent to background
   */
  isTaskSentToBackground: (taskId) => {
    const state = get();
    return state.tasks[taskId]?.sentToBackground === true;
  },

  /**
   * Mark a task as completed and move to newContent
   */
  completeTask: (taskId, result) => set((state) => {
    const task = state.tasks[taskId];
    if (!task) return state;
    
    const completedTask = {
      ...task,
      status: TASK_STATUS.COMPLETED,
      completedAt: Date.now(),
      result,
    };
    
    // Remove from active tasks, add to newContent
    const { [taskId]: removed, ...remainingTasks } = state.tasks;
    
    return {
      tasks: remainingTasks,
      newContent: [completedTask, ...state.newContent],
    };
  }),

  /**
   * Mark a task as failed
   */
  failTask: (taskId, error) => set((state) => {
    const task = state.tasks[taskId];
    if (!task) return state;
    
    const failedTask = {
      ...task,
      status: TASK_STATUS.FAILED,
      completedAt: Date.now(),
      error: userFacingError(error, 'Generation could not be completed. Please try again.'),
    };
    
    // Remove from active tasks, add to newContent (so user knows it failed)
    const { [taskId]: removed, ...remainingTasks } = state.tasks;
    
    return {
      tasks: remainingTasks,
      newContent: [failedTask, ...state.newContent],
    };
  }),

  /**
   * Dismiss a single item from new content
   */
  dismissNewContent: (taskId) => set((state) => ({
    newContent: state.newContent.filter((item) => item.id !== taskId),
  })),

  /**
   * Clear all new content
   */
  clearAllNewContent: () => set({ newContent: [] }),

  /**
   * Remove a pending task (e.g., if user cancels while still loading)
   */
  removeTask: (taskId) => set((state) => {
    const { [taskId]: removed, ...remainingTasks } = state.tasks;
    return { tasks: remainingTasks };
  }),

  /**
   * Get task by scene and keyframe ID (for matching realtime updates)
   */
  getTaskByKeyframe: (sceneId, keyframeId) => {
    const state = get();
    return Object.values(state.tasks).find(
      (task) => 
        task.sceneId === sceneId && 
        (task.keyframeId === keyframeId || task.pendingKeyframeId === keyframeId) &&
        (task.type === TASK_TYPES.KEYFRAME_CREATE || task.type === TASK_TYPES.KEYFRAME_EDIT)
    );
  },

  /**
   * Get task by asset ID (for matching realtime updates)
   */
  getTaskByAsset: (assetId) => {
    const state = get();
    return Object.values(state.tasks).find(
      (task) => 
        (task.assetId === assetId || task.pendingAssetId === assetId) &&
        (task.type === TASK_TYPES.ASSET_CREATE || task.type === TASK_TYPES.ASSET_EDIT)
    );
  },

        /**
         * Find task by scene (for newly created keyframes where we don't know the ID yet)
         */
        findPendingKeyframeTaskForScene: (sceneId, projectId = null) => {
          const state = get();
          return Object.values(state.tasks).find(
            (task) =>
              task.sceneId === sceneId &&
              task.type === TASK_TYPES.KEYFRAME_CREATE &&
              task.status === TASK_STATUS.PENDING &&
              !task.keyframeId &&
              (!projectId || !task.projectId || task.projectId === projectId)
          );
        },

        /**
         * Find pending project-level keyframe creation task
         */
        findPendingProjectKeyframeTask: (projectId = null) => {
          const state = get();
          return Object.values(state.tasks).find(
            (task) =>
              task.sceneId == null &&
              task.type === TASK_TYPES.KEYFRAME_CREATE &&
              task.status === TASK_STATUS.PENDING &&
              !task.keyframeId &&
              (!projectId || !task.projectId || task.projectId === projectId)
          );
        },

        /**
         * Find pending asset creation task
         */
        findPendingAssetTask: (projectId = null) => {
          const state = get();
          return Object.values(state.tasks).find(
            (task) =>
              task.type === TASK_TYPES.ASSET_CREATE &&
              task.status === TASK_STATUS.PENDING &&
              !task.assetId &&
              (!projectId || !task.projectId || task.projectId === projectId)
          );
        },

  /**
   * Get all pending tasks
   */
  getPendingTasks: () => {
    const state = get();
    return Object.values(state.tasks).filter((task) => task.status === TASK_STATUS.PENDING);
  },

  /**
   * Get count of pending tasks
   */
  getPendingCount: () => {
    const state = get();
    return Object.values(state.tasks).filter((task) => task.status === TASK_STATUS.PENDING).length;
  },

        /**
         * Reset store
         */
        resetBackgroundTasks: () => set(() => initialState),
      }),
      {
        name: STORAGE_KEY,
        version: 1,
        partialize: (state) => ({
          tasks: state.tasks,
          newContent: state.newContent,
        }),
        migrate: (persistedState) => {
          const state = persistedState?.state || persistedState || initialState;
          return pruneStaleTasks(state);
        },
      }
    ),
    { name: 'BackgroundTasksStore' }
  )
);

